import type { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { isString } from "./type-utils";

export const generatePassengerFromUrl = (rooms: string | undefined) => {
  const roomArr = [];
  if (rooms) {
    const perRoom = rooms?.split("_");
    let splitedP = [];
    let childArr = [];
    for (let i = 0; i < perRoom.length; i++) {
      splitedP = perRoom[i].split("-");
      if (splitedP.length > 1) {
        for (let j = 1; j < splitedP.length; j++) {
          childArr.push(splitedP[j]);
        }
      }
      roomArr.push({
        adult: splitedP[0],
        child: childArr,
      });
      childArr = [];
    }
  }
  return roomArr;
};

/**
 * Creates a URL with appended search parameters.
 * @param url - The base URL string (e.g., "https://example.com").
 * @param searchParams - The search parameters as a string or URLSearchParams.
 * @returns A string representing the full URL with search parameters.
 * @throws {TypeError} If url is not a string or searchParams is invalid.
 */
export const createUrlWithSearchParams = (
  url: string,
  searchParams: string | URLSearchParams,
): string => {
  if (!isString(url) || url.trim() === "") {
    throw new TypeError("URL must be a non-empty string");
  }

  const paramsString = searchParams.toString();
  return paramsString ? `${url}?${paramsString}` : url;
};

/**
 * Creates or updates a URLSearchParams object with new parameters.
 * @param baseSearchParams - The base search parameters (string or URLSearchParams).
 * @param newParams - Array of objects containing name-value pairs to add.
 * @returns A URLSearchParams object with the combined parameters.
 * @throws {TypeError} If baseSearchParams is not a string or URLSearchParams instance.
 */
export const createSearchParams = (
  baseSearchParams: string | URLSearchParams,
  newParams: { name: string; value: string }[],
): URLSearchParams => {
  const searchParams = isString(baseSearchParams)
    ? new URLSearchParams(baseSearchParams)
    : baseSearchParams instanceof URLSearchParams
      ? baseSearchParams
      : new URLSearchParams();

  for (const { name, value } of newParams) {
    if (isString(name) && isString(value)) {
      searchParams.set(name, value);
    }
  }

  return searchParams;
};

/**
 *
 * @param pathName - The path name to update.
 * @param searchparams - The search parameters to update.
 * @param param - The parameter to update.
 * @param replace - The function to replace the URL.
 */

export function updateSearchparams(
  pathName: string,
  searchparams: ReadonlyURLSearchParams,

  params:
    | {
        key: string;
        value: string;
      }
    | {
        key: string;
        value: string;
      }[],
  replace: (href: string, options?: NavigateOptions) => void,
) {
  const readonlyParams = new URLSearchParams(searchparams);

  if (Array.isArray(params)) {
    for (const { key, value } of params) {
      readonlyParams.set(key, value);
    }
  } else {
    readonlyParams.set(params.key, params.value);
  }

  replace(`${pathName}?${new URLSearchParams(readonlyParams).toString()}`, {
    scroll: false,
  });
}

/**
 *
 * @param activeRoutes - active routes that should match with pathName
 * @param pathName - pathName that should match with activeRoutes
 * @returns boolean indicating if the path is active
 */

export function isActivePath(activeRoutes: string[], pathName: string) {
  const normalizePath = (path: string) => `${path.replace(/\/$/, "")}/`;

  const normalizedPathName = normalizePath(pathName);

  return activeRoutes.some((route) => {
    const normalizedRoute = normalizePath(route);

    if (normalizedRoute.includes(":slug") || normalizedRoute.includes("*")) {
      // Replace :slug or * with a regex to match any segment
      const routePattern = normalizedRoute
        .replace(":slug", "[^/]+") // Match any non-slash characters
        .replace("*", "[^/]+") // Same for wildcard
        .replace(/\//g, "\\/"); // Escape slashes for regex
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(normalizedPathName);
    }

    return normalizedRoute === normalizedPathName;
  });
}
